/**
 * @file useChatSdkContext
 * @description Chat SDK Context 和 Provider
 *
 * 提供全局共享的 hostApi，供 SDK 初始化时使用。
 */

import '@baidu/chat-app-manju/style';
import {createContext, useContext, useMemo, useRef, useCallback} from 'react';
import message from '@/components/common/Message';
import {selectAndUploadFiles} from '@/utils/selectAndUpload';
import {downloadFile} from '@/utils/downloadFile';
import {showMediaPreview} from '@/utils/mediaPreview';
import {copyTextToClipboard} from '@/utils';
import {getCurrentProjectId} from '@/utils/projectId';
import {openMembershipModal} from '@/commerce/modals/openModal';
import type {MembershipModalSourceType} from '@/commerce/modals/MembershipModal/types';
import {sendLog} from '@/services/tracking';
import type {LogParams, SendLogOptions} from '@/services/tracking';
import type {
    UploadSource,
    UploadFileTypeOption,
    UploadOptions,
    UploadFileItem,
    SubmitResult,
    DownloadOptions,
} from '@/types/file';

interface ChatSdkContextValue {
    /** 共享 HostApi（showToast、previewImage 等） */
    hostApi: Record<string, (...args: any[]) => any>;
    /** 动态设置上传场景 */
    setUploadScene: (scene: UploadSource) => void;
}

// 共享 HostApi

function createSharedHostApi(sceneRef: React.MutableRefObject<UploadSource>) {
    const showToast = ({
        message: msg,
        type = 'info',
    }: {
        message: string;
        type?: 'info' | 'success' | 'error' | 'warning';
    }) => {
        message[type](msg);
    };

    return {
        // 预览内容（单个图片/视频）
        preview: ({url, type = 'image'}: {url: string; type?: 'image' | 'video'}) => {
            showMediaPreview({src: url, type, closeOnClickBlank: true});
        },

        showToast,

        copy: async (text: string) => {
            const {ok, msg} = await copyTextToClipboard(text);
            showToast({
                message: msg,
                type: ok ? 'success' : 'error',
            });
        },

        download: (options: DownloadOptions) => {
            downloadFile(options);
        },

        // 打开会员订阅模态（SDK 传对象参数，解构 source 后透传）
        openMemberSubscriptionModal: (params?: {source?: string}) => {
            openMembershipModal(params?.source as MembershipModalSourceType | undefined);
        },

        /**
         * 文件上传（支持多选）
         *
         * @param type 上传类型（IMAGE / VIDEO / AUDIO / TXT / AUTO）
         * @param options 上传配置（multiple/maxCount/allowedExtensions/maxSizeByte 等）
         * @param files 已获取到的文件列表；传入时不打开文件选择弹窗，直接上传
         * @param onUpload 文件状态变化回调，每次推送完整 UploadFileItem 列表快照
         * @returns SubmitResult 包含成功与失败的文件项；用户取消选择时返回 `{succeeded: [], failed: []}`
         *
         * @example
         * // 1) 仅获取最终结果
         * const {succeeded, failed} = await hostApi.upload({
         *     type: 'IMAGE',
         *     options: {multiple: true, maxCount: 9},
         * });
         * if (!succeeded.length && !failed.length) {
         *     // 用户取消选择
         *     return;
         * }
         * succeeded.forEach((item) => console.log(item.response?.fileUrl));
         *
         * @example
         * // 2) 实时渲染上传进度（onUpload 推送整列表快照）
         * const [list, setList] = useState<UploadFileItem[]>([]);
         * await hostApi.upload({
         *     type: 'AUTO',
         *     onUpload: setList, // 直接用快照覆盖即可，无需自行 merge
         * });
         *
         * @example
         * // 3) 区分校验失败 / 上传失败
         * const {succeeded, failed} = await hostApi.upload({type: 'VIDEO'});
         * failed.forEach(({error}) => {
         *     if (error?.stage === 'VALIDATE') {
         *         // 文件类型/大小不合法（已自动 toast，无需重复提示）
         *     } else {
         *         // 网络/服务端错误（UPLOAD / CHUNK_* 阶段）
         *     }
         * });
         */
        upload: ({
            type,
            scene,
            options,
            files,
            onUpload,
        }: {
            type: UploadFileTypeOption;
            scene?: UploadSource;
            options?: UploadOptions;
            files?: File[] | FileList;
            onUpload?: (items: UploadFileItem[]) => void;
        }): Promise<SubmitResult> => {
            const notified = new Set<string>();
            return selectAndUploadFiles({
                files,
                type,
                scene: scene ?? sceneRef.current,
                options,
                projectId: getCurrentProjectId(),
                onUpload: (items) => {
                    items.forEach((item) => {
                        if (item.status === 'ERROR' && item.error && !notified.has(item.id)) {
                            showToast({message: item.error.message, type: 'error'});
                            notified.add(item.id);
                        }
                    });
                    onUpload?.(items);
                },
            });
        },
        /**
         * 发送埋点日志
         *
         * @param params 日志参数（type/value/from/source/ext 等）
         * @param options 发送选项（useSendBeacon/useXhr/sendMethod）
         *
         * @example
         * hostApi.sendLog({type: 'click', value: 'submit', from: 'agent-input'});
         */
        sendLog: (params: LogParams, options?: SendLogOptions) => {
            sendLog(params, options);
        },
    };
}

const ChatSdkContext = createContext<ChatSdkContextValue | null>(null);

/**
 * ChatSdkProvider
 *
 * 提供全局共享的 hostApi。
 */
export function ChatSdkProvider({children}: {children: React.ReactNode}) {
    const sceneRef = useRef<UploadSource>('CANVAS_AGENT');

    const hostApi = useMemo(() => createSharedHostApi(sceneRef), []);

    const setUploadScene = useCallback((scene: UploadSource) => {
        sceneRef.current = scene;
    }, []);

    const value = useMemo(() => ({hostApi, setUploadScene}), [hostApi, setUploadScene]);

    return <ChatSdkContext.Provider value={value}>{children}</ChatSdkContext.Provider>;
}

/**
 * 获取 ChatSdkContext 的 Hook
 */
export function useChatSdkContext() {
    const ctx = useContext(ChatSdkContext);
    if (!ctx) {
        throw new Error('useChatSdkContext must be used within ChatSdkProvider');
    }
    return ctx;
}
